import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [services, setServices] = useState([]);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  const navLinks = [
    { name: 'Forside', href: '/' },
    { name: 'Om os', href: '/#om-os' },
    { name: 'Projekter', href: '/projekter', isSpa: true },
    { name: 'Kontakt', href: '/#kontakt' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 bg-white shadow-sm ${
        scrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="https://assets.auxodev.dk/assets/10d78498-60e6-4cb8-8f48-f81fe0e15f7b/eb15e459-943c-45ab-9d1d-87b7e79f3040.png" 
              alt="SL. Flise & Murer Logo" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-lg font-medium transition-colors hover:text-orange-600 text-slate-600"
            >
              Forside
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger asChild>
                <button className="flex items-center text-lg font-medium transition-colors hover:text-orange-600 text-slate-600 cursor-pointer outline-none">
                  Ydelser
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[220px] bg-white rounded-md p-2 shadow-xl border border-slate-100 z-50 animate-in fade-in zoom-in duration-200"
                  sideOffset={5}
                >
                  {services.map((service) => (
                    <DropdownMenu.Item key={service.id} className="outline-none">
                      <Link
                        to={`/ydelser/${service.slug.da}`}
                        className="flex items-center px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-orange-600 rounded-sm transition-colors"
                      >
                        {service.title.da}
                      </Link>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {navLinks.slice(1).map((link) => {
              if (link.isSpa) {
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-lg font-medium transition-colors hover:text-orange-600 text-slate-600"
                  >
                    {link.name}
                  </Link>
                );
              }
              return (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-lg font-medium transition-colors hover:text-orange-600 text-slate-600"
                >
                  {link.name}
                </a>
              );
            })}
            <a
              href="#kontakt"
              className="bg-orange-600 text-white px-6 py-2.5 rounded-full text-base font-semibold hover:bg-orange-700 transition-all"
            >
              Få et tilbud
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-slate-900"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] md:hidden"
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-[300px] bg-white z-[70] shadow-2xl md:hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="text-lg font-bold text-slate-900">Menu</span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-6 px-4 space-y-2">
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 text-lg font-medium text-slate-700 hover:text-orange-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Forside
                </Link>

                {/* Mobile Services Accordion */}
                <div className="space-y-1">
                  <button
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                    className="flex items-center justify-between w-full px-4 py-3 text-lg font-medium text-slate-700 hover:text-orange-600 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Ydelser
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {isMobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden bg-slate-50 rounded-lg ml-2"
                      >
                        <div className="py-2 space-y-1">
                          {services.map((service) => (
                            <Link
                              key={service.id}
                              to={`/ydelser/${service.slug.da}`}
                              onClick={() => setIsOpen(false)}
                              className="block px-6 py-2.5 text-base text-slate-600 hover:text-orange-600 transition-colors"
                            >
                              {service.title.da}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {navLinks.slice(1).map((link) => {
                  if (link.isSpa) {
                    return (
                      <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-3 text-lg font-medium text-slate-700 hover:text-orange-600 hover:bg-slate-50 rounded-lg transition-colors"
                      >
                        {link.name}
                      </Link>
                    );
                  }
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 text-lg font-medium text-slate-700 hover:text-orange-600 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      {link.name}
                    </a>
                  );
                })}
              </div>

              <div className="p-4 border-t border-slate-100">
                <a
                  href="#kontakt"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center bg-orange-600 text-white px-6 py-4 rounded-xl text-lg font-bold shadow-lg shadow-orange-600/20 hover:bg-orange-700 transition-all active:scale-[0.98]"
                >
                  Få et tilbud
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
